import { getLinkFieldUrl } from '../../data/prismic-helper'
import { InlineCaption, InlineElementContainer } from '../atoms/Atoms.tsx'
import { ScrollstoryDocumentDataBodyVideoSlicePrimary } from '../../../types.generated'
import { asText } from '@prismicio/client'
import { VideoPlayer } from '../video/VideoPlayer.tsx'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from '@emotion/styled'
import {
  VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP,
  VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE,
} from '../../theme/theme.tsx'
import { BREAKPOINT_MOBILE, useIsPortrait } from '../../util/breakpoints.ts'
import { getPlayingThreshold } from './util.ts'

export interface InlineVideoProps {
  video: ScrollstoryDocumentDataBodyVideoSlicePrimary
  // play_once is used for short animations as videos (muted, no loop)
  // on_click is used for optional videos (unmuted, no loop)
  // autoplay is used for looped muted videos
  playback: 'on_click' | 'autoplay' | 'play_once'
  type: 'inline' | 'oversized'
  lang: string
}

const getVideoURL = (
  data: ScrollstoryDocumentDataBodyVideoSlicePrimary,
  isPortrait: boolean
): string => {
  if (isPortrait) {
    const portraitVideoURL = getLinkFieldUrl(data.video_portrait)
    if (portraitVideoURL) {
      return portraitVideoURL
    }
  }

  const videoUrl = getLinkFieldUrl(data.video)
  if (!videoUrl) {
    throw new Error('Video URL is not defined')
  }

  return videoUrl
}

export default function InlineVideo(props: InlineVideoProps) {
  const isPortrait = useIsPortrait()
  const [playing, setPlaying] = useState<boolean>(false)
  const [hasEnded, setHasEnded] = useState<boolean>(false)
  const { ref, inView } = useInView({
    threshold: getPlayingThreshold(isPortrait, props.video),
  })

  const caption = asText(props.video.media_caption)
  const source = asText(props.video.media_source)
  const videoUrl = getVideoURL(props.video, isPortrait)

  useEffect(() => {
    if (props.playback === 'autoplay' || props.playback === 'play_once') {
      if (props.playback === 'play_once' && hasEnded) {
        setPlaying(false)
      } else {
        setPlaying(inView)
      }
    }
  }, [inView, hasEnded, props.playback])

  const videoElement = (
    <VideoPlayer
      videoUrl={videoUrl}
      playing={playing}
      controls={props.playback === 'on_click' ? 'browserDefault' : 'none'}
      muted={props.playback === 'autoplay' || props.playback === 'play_once'}
      loop={props.playback === 'autoplay'}
      alt={props.video.alt_text ?? ''}
      subtitleLang={props.lang}
      onEnded={() => setHasEnded(true)}
    />
  )

  if (props.type !== 'oversized') {
    return (
      <InlineElementContainer ref={ref}>
        {videoElement}
        <InlineCaption>
          {caption} <i>{source}</i>
        </InlineCaption>
      </InlineElementContainer>
    )
  } else {
    return (
      <OversizedVideoContainer ref={ref}>
        {videoElement}
      </OversizedVideoContainer>
    )
  }
}

const OversizedVideoContainer = styled.div`
  width: 100vw;

  margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP};
  @media ${BREAKPOINT_MOBILE} {
    margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE};
  }
`
